import {
  AdditionalBuilding,
  F2100AdditionalBuildingType,
} from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/AdditionalBuildings/additionalBuildingsInterfaces";

import { CABIN_ABOUT_STEP } from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/Cabin";
import { CABIN_INTRO_STEP } from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/Cabin/cabinSteps";
import { CabinProductDetailsFormValues } from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/Cabin/ProductDetails/cabinProductDetailsInterfaces";
import { CabinProductIntroFormValues } from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/Cabin/ProductIntro/cabinProductIntroInterfaces";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { mapFormToInjuries } from "Store/priceCalculation/productMappers/common/injuriesMapper";
import { nullableFormBooleanToNullableBoolean } from "Utils/formUtils";

interface MappedAdditionalBuilding {
  constructionYear: string;
  coverage: string;
  grossArea: number;
  type: string;
  isIsolated?: boolean;
  livingFloorSpace?: number;
  hasTapWater?: boolean;
  hasElectricity?: boolean;
  hasRental?: boolean;
  standard?: string;
}

export type CabinProductBackend = {
  name: INSURANCE_NAME_VALUE;
  coverageGroup: any;
  address: {
    zipCode: string;
    city?: string;
  };
  constructionYear: number;
  crawlspace: boolean;
  grossArea: number;
  livingFloorSpace: number;
  standard: string;
  hasElectricity: boolean;
  hasRental?: boolean;
  hasTapWater: boolean;
  previousFungusDamage: boolean;
  additionalBuildings: MappedAdditionalBuilding[];
  damageCountRecentYears: number;
  yearOfLastDamage?: number;
  coverageAmountCabinContent?: number;
  hasAdditionalBuildings?: boolean;
};

export function mapToCommerceCabin(data: {
  [key: string]: any;
}): CabinProductBackend {
  const introCabin: CabinProductIntroFormValues =
    data.formData[`${CABIN_INTRO_STEP.id}`];
  const aboutCabin: CabinProductDetailsFormValues =
    data.formData[`${CABIN_ABOUT_STEP.id}`];

  const mapAdditionalBuildings = introCabin.otherBuildings.map(
    (building: AdditionalBuilding): MappedAdditionalBuilding => {
      return {
        constructionYear: building.constructionYear,
        coverage: "Standard",
        grossArea: Number(building.areal),
        type: building.type,
        isIsolated: building.isolated ?? undefined,
        ...(building.type === F2100AdditionalBuildingType.ANNEX && {
          livingFloorSpace: building.boa ? Number(building.boa) : undefined,
          hasTapWater: building.hasTapWater,
          hasElectricity: building.hasElectricity,
          standard: building.standard,
        }),
      };
    },
  );

  return {
    name: INSURANCE_NAME_VALUE.CABIN,
    coverageGroup: data.coverage,
    address: {
      zipCode: introCabin.postNumber.toString(),
      city: introCabin.city,
    },
    constructionYear: Number(introCabin.constructionYear),
    grossArea: Number(introCabin.grossArea),
    livingFloorSpace: Number(introCabin.livingArea),
    standard: aboutCabin.standard,
    hasElectricity:
      nullableFormBooleanToNullableBoolean(aboutCabin.hasElectricity) ?? false,
    hasRental: nullableFormBooleanToNullableBoolean(aboutCabin.hasRental),
    hasTapWater:
      nullableFormBooleanToNullableBoolean(aboutCabin.hasTapWater) ?? false,
    crawlspace:
      nullableFormBooleanToNullableBoolean(aboutCabin.hasCrawlspace) ?? false,
    previousFungusDamage:
      nullableFormBooleanToNullableBoolean(aboutCabin.pestsDamaged) ?? false,
    additionalBuildings: introCabin.otherBuildings
      ? mapAdditionalBuildings
      : [],
    hasAdditionalBuildings: mapAdditionalBuildings.length > 0,
    ...mapFormToInjuries(aboutCabin.injuries, aboutCabin.injuryYear),
  };
}
